import { Stack, TextField } from '@mui/material';
import {
  useState, useRef, useEffect, memo, useCallback,
} from 'react';
import { useClickAway } from 'react-use';
import NodeMenus from './NodeMenus';
import { textFieldStyles, nodeLabelStackStyles } from '../utils/styles';
import { isLeafNode } from '../utils/helpers';

function NodeLabel({
  node,
  composedNodeId,
  anchorEl,
  selectedNodeId,
  handleNodeSelect,
  handleMenuOpen,
  handleMenuClose,
  handleAction,
  levelMap,
  depth,
  maxDepth,
}) {
  const [editMode, setEditMode] = useState(!node.name);
  const [editText, setEditText] = useState(node.name || '');
  const [isHovered, setIsHovered] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editMode && inputRef.current) {
      inputRef.current.focus();
      handleNodeSelect(composedNodeId);
    }
  }, [editMode, composedNodeId, handleNodeSelect]);

  useClickAway(inputRef, () => {
    if (!editMode) return;

    setEditMode(false);
    const trimmed = editText.trim();
    if (trimmed) {
      handleAction('edit', node.id, trimmed);
    } else if (node.name) {
      setEditText(node.name);
    } else {
      handleAction('delete', node.id);
    }
  });

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    setEditMode(true);
    handleNodeSelect(composedNodeId);
  }, [composedNodeId, handleNodeSelect]);

  const isLeaf = isLeafNode(node, maxDepth);
  const allowAddFeatures = !node.children?.length;

  return (
    <Stack
      gap={2}
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={nodeLabelStackStyles}
    >
      <TextField
        inputRef={inputRef}
        variant="outlined"
        placeholder={levelMap[node.level] || 'New Node'}
        fullWidth
        value={editText}
        onChange={(e) => setEditText(e.target.value)}
        onClick={handleClick}
        sx={{
          ...textFieldStyles,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: selectedNodeId === composedNodeId ? 'primary.main' : 'grey.300',
            borderWidth: selectedNodeId === composedNodeId ? 1.5 : 1,
          },
        }}
        InputProps={{
          endAdornment: (
            <NodeMenus
              nodeId={node.id}
              depth={depth}
              composedNodeId={composedNodeId}
              anchorEl={anchorEl}
              handleMenuClose={handleMenuClose}
              handleMenuOpen={handleMenuOpen}
              handleAction={handleAction}
              handleNodeSelect={handleNodeSelect}
              isHovered={isHovered}
              isLeafNode={isLeaf}
              allowAddFeatures={allowAddFeatures}
              isSelected={selectedNodeId === composedNodeId}
              inputRef={inputRef}
            />
          ),
        }}
      />
    </Stack>
  );
}

export default memo(NodeLabel);
